import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell } from "recharts";
import { EmptyState, Pill } from "./AdminUI";

const COLORS = ["#2E6B57", "#EF4444"];

export default function AuditTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/audit").then(r => setData(r.data)); }, []);
  if (!data) return <p className="text-sm text-[#222]/45">Loading…</p>;

  return (
    <div data-testid="admin-audit-tab">
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-black/5">
          <h3 className="text-sm font-bold mb-3">Actions by type</h3>
          {data.by_action.length === 0 ? <p className="text-sm text-[#222]/40 py-8 text-center">No actions logged yet</p> : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.by_action.slice(0, 8)} layout="vertical" margin={{ left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10 }} />
                <YAxis type="category" dataKey="name" width={170} tick={{ fontSize: 9 }} />
                <Tooltip />
                <Bar dataKey="value" fill="#2E6B57" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-black/5">
          <h3 className="text-sm font-bold mb-3">Success vs failure</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={data.by_result} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75} label>
                {data.by_result.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {data.entries.length === 0 ? (
        <EmptyState title="No audit records" body="Every sensitive Super Admin action appears here, permanently — nothing can be edited or deleted." testid="admin-audit-empty" />
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-black/5 overflow-hidden">
          <table className="w-full text-xs" data-testid="admin-audit-table">
            <thead className="bg-black/[0.03] uppercase tracking-wider text-[#222]/50">
              <tr>
                <th className="text-left px-4 py-3">Action</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">Target</th>
                <th className="text-left px-4 py-3">Reason</th>
                <th className="text-left px-4 py-3">Admin</th>
                <th className="text-left px-4 py-3">Result</th>
                <th className="text-right px-4 py-3">When</th>
              </tr>
            </thead>
            <tbody>
              {data.entries.map(e => (
                <tr key={e.id} className="border-t border-black/5" data-testid={`admin-audit-row-${e.id}`}>
                  <td className="px-4 py-3 font-semibold">{e.action}</td>
                  <td className="px-4 py-3 hidden md:table-cell text-[#222]/60">{e.target_label || e.target_id || "—"}</td>
                  <td className="px-4 py-3 text-[#222]/60">{e.reason || "—"}</td>
                  <td className="px-4 py-3 text-[#222]/60">{e.admin_email}</td>
                  <td className="px-4 py-3"><Pill tone={e.result === "failure" ? "red" : "green"}>{e.result || "success"}</Pill></td>
                  <td className="px-4 py-3 text-right text-[#222]/40 whitespace-nowrap">{new Date(e.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-[11px] text-[#222]/40 mt-3">Audit records are immutable — there is no edit or delete action anywhere in this dashboard.</p>
    </div>
  );
}
